import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { keepsScroll } from './scrollIntent';

/**
 * How long a link's `#section` may take to render before we stop waiting:
 * generous for a slow phone, since a reader who scrolls meanwhile keeps
 * their place.
 */
const HASH_TARGET_WAIT_MS = 10000;
const HASH_TARGET_POLL_MS = 50;
/**
 * The page moving this far while we wait means the reader scrolled. Measured
 * rather than inferred from wheel or touch events: a trackpad's momentum
 * after a flick-and-click sends those without moving the new page.
 */
const READER_MOVED_PX = 8;

/**
 * Brings a link's `#section` to the top of the viewport once it renders,
 * leaving the section's own `scroll-margin-top` above it for the sticky
 * header. Returns the cleanup.
 */
function scrollToHashTarget(hash: string): () => void {
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    // A malformed escape: look the id up as written.
  }
  const startY = window.scrollY;
  const deadline = Date.now() + HASH_TARGET_WAIT_MS;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const tryScroll = () => {
    if (Math.abs(window.scrollY - startY) > READER_MOVED_PX) return;
    const target = id ? document.getElementById(id) : null;
    if (target) {
      const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - margin,
        left: 0,
        behavior: 'instant',
      });
    } else if (Date.now() < deadline) {
      timer = setTimeout(tryScroll, HASH_TARGET_POLL_MS);
    }
  };
  tryScroll();
  return () => clearTimeout(timer);
}

/**
 * Where every navigation lands: a new page at the top, a link to a section
 * at that section, back and forward where the browser left the reader.
 * Pages never scroll themselves to the top on mount: that undoes both a
 * section link and a back/forward restore.
 */
const ScrollToTop = () => {
  const { pathname, hash, key, state } = useLocation();
  const navigationType = useNavigationType();
  // Read, not a dependency: a page's own state change alone never scrolls.
  const stateRef = useRef<unknown>(state);
  stateRef.current = state;
  const keyRef = useRef(key);
  keyRef.current = key;
  // The history entry the page loaded at, until the reader leaves it. Keyed
  // rather than a first-run flag, so StrictMode's second effect run still
  // counts as the load.
  const loadKeyRef = useRef<string | null>(key);

  useEffect(() => {
    if (keyRef.current !== loadKeyRef.current) loadKeyRef.current = null;
    const firstLoad = loadKeyRef.current !== null;
    // A link to a section of a page (`/english#…`) opens at that section.
    // The browser's own jump is lost on a first load, because React replaces
    // the prerendered page, and never happens on an in-app push.
    if (hash && (firstLoad || navigationType === 'PUSH')) {
      if (!firstLoad) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return scrollToHashTarget(hash);
    }
    // Preserve scroll on browser back/forward so returning to a list page
    // lands where the user left off. Only reset on fresh PUSH/REPLACE nav,
    // and never for a page's own URL bookkeeping (`withKeptScroll`).
    if (navigationType === 'POP') return;
    // Only a replace is bookkeeping; a push that forwards the state still
    // opens a new page at the top.
    if (navigationType === 'REPLACE' && keepsScroll(stateRef.current)) return;
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, [pathname, hash, navigationType]);

  return null;
};

export default ScrollToTop;
